/* Slick Willys — cart drawer controller.
   Phase 1: open/close, focus trap, body scroll lock, Esc/backdrop/CLOSE dismiss,
   bag-icon click intercept, aria-hidden toggling, prefers-reduced-motion respect.
   No cart mutations yet — those land in Phase 3. */

(function () {
  'use strict';

  var BAG_LINK_SELECTOR = '.cart-icon a, a[href="/cart"], a[href$="/cart"]';
  var CLOSE_SELECTOR = '[data-cart-close]';
  var FOCUSABLE_SELECTORS = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var drawer = null;
  var panel = null;
  var lastFocused = null;
  var savedScrollY = 0;
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function isOpen() {
    return drawer && drawer.getAttribute('aria-hidden') === 'false';
  }

  function lockScroll() {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.classList.add('sw-cart-locked');
  }

  function unlockScroll() {
    document.body.classList.remove('sw-cart-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  function focusableEls() {
    if (!panel) return [];
    var nodes = panel.querySelectorAll(FOCUSABLE_SELECTORS);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      // Skip elements inside display:none subtrees.
      if (n.offsetParent === null && getComputedStyle(n).position !== 'fixed') continue;
      out.push(n);
    }
    return out;
  }

  function trapTab(e) {
    if (e.key !== 'Tab') return;
    var els = focusableEls();
    if (els.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    var first = els[0];
    var last = els[els.length - 1];
    var active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || active === panel) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function onKeydown(e) {
    if (!isOpen()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') trapTab(e);
  }

  // Motion on open: sweep the free-ship bar from empty to its current value.
  // Item stagger + subtotal count-up were tried and pulled — bar only.
  function playOpenMotion() {
    if (reducedMotion || !panel) return;
    var bar = panel.querySelector('[data-qualifier-bar]');
    if (!bar) return;
    var target = bar.style.width || '';
    // Snap to empty NOW, while the drawer is still sliding in, so the bar's
    // server-rendered full state is never seen (no full→0→full flash).
    bar.style.transition = 'none';
    bar.style.width = '0%';
    void bar.offsetWidth;                          // reflow so 0% commits
    // Then sweep to the real value once the drawer has landed.
    window.setTimeout(function () {
      bar.style.transition = 'width 2s cubic-bezier(.25, .8, .25, 1)';
      bar.style.width = target;
    }, 300);
  }

  /* ───────────────────── Phase 3: cart mutations ─────────────────────
     Qty +/− and REMOVE hit /cart/change.js with `sections: 'cart-drawer'`, so
     the server returns freshly-rendered drawer HTML (approach B). No client-side
     money math — subtotal, qualifier and Afterpay come back rendered. One
     mutation at a time; optimistic touch, roll back + inline message on failure. */

  var pending = false;

  function cssEsc(v) {
    return (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&');
  }

  function lineRow(key) {
    if (!panel) return null;
    return panel.querySelector('[data-cart-item][data-line-key="' + cssEsc(key) + '"]');
  }

  function pull(live, fresh, sel, mode) {
    var a = live.querySelector(sel), b = fresh.querySelector(sel);
    if (!a || !b) return;
    if (mode === 'html') a.innerHTML = b.innerHTML;
    else if (mode === 'class') a.className = b.className;
    else a.textContent = b.textContent;
  }

  // Reconcile the live drawer with freshly-rendered section HTML WITHOUT blowing
  // the footer away. Critical: the Shop Pay button is a Shopify web component
  // (shadow DOM) — re-creating it on every change makes it re-init and flash, and
  // a wholesale innerHTML swap repaints the whole footer. So: replace the body
  // (line items — no shadow DOM, safe) and copy footer VALUES in place, leaving
  // Shop Pay + CHECKOUT nodes alone.
  function applySectionHTML(html) {
    if (!drawer || !html) return;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]');
    if (!fresh) return;
    var freshPanel = fresh.querySelector('[data-cart-drawer-panel]');
    if (!panel || !freshPanel) {                 // unrecognised structure — full swap
      drawer.innerHTML = fresh.innerHTML;
      panel = drawer.querySelector('[data-cart-drawer-panel]');
      return;
    }

    pull(panel, freshPanel, '[data-cart-count]');

    // Body: line items or empty state.
    var lb = panel.querySelector('[data-cart-body]'), fb = freshPanel.querySelector('[data-cart-body]');
    if (lb && fb) lb.innerHTML = fb.innerHTML;

    // Footer: reconcile in place, or add/remove on empty↔items transitions.
    var lf = panel.querySelector('.vA-foot'), ff = freshPanel.querySelector('.vA-foot');
    if (lf && ff) {
      var lq = lf.querySelector('[data-cart-qualifier]'), fq = ff.querySelector('[data-cart-qualifier]');
      if (lq && fq) lq.setAttribute('data-qualified', fq.getAttribute('data-qualified'));
      pull(lf, ff, '[data-qualifier-label]');
      pull(lf, ff, '[data-qualifier-delta]', 'html');
      pull(lf, ff, '[data-qualifier-delta]', 'class');
      // Bar: revert any leftover open-sweep transition so qty updates glide at
      // the normal .35s (CSS), not the 2s open sweep.
      var lbar = lf.querySelector('[data-qualifier-bar]'), fbar = ff.querySelector('[data-qualifier-bar]');
      if (lbar && fbar) { lbar.style.transition = ''; lbar.style.width = fbar.style.width; }
      // Discount row(s): container always exists in the section, so a plain
      // HTML copy handles appear / amount change / disappear alike.
      pull(lf, ff, '[data-cart-discounts]', 'html');
      pull(lf, ff, '[data-cart-subtotal]');
      // Afterpay: appear / update amount / disappear — Shop Pay + CHECKOUT untouched.
      var la = lf.querySelector('[data-afterpay]'), fa = ff.querySelector('[data-afterpay]');
      if (la && fa) pull(lf, ff, '[data-afterpay-amount]');
      else if (!la && fa) {
        var form = lf.querySelector('.vA-checkoutForm');
        if (form) form.parentNode.insertBefore(fa, form);
      } else if (la && !fa) { la.remove(); }
    } else if (!lf && ff) {
      panel.appendChild(ff);        // cart became non-empty
    } else if (lf && !ff) {
      lf.remove();                  // cart became empty
    }
  }

  // Re-render the drawer — used after ATC (we don't own the add request) and to
  // reconcile after an error. Uses the Ajax Cart API's section rendering via a
  // no-op /cart/update.js (empty update, changes nothing) rather than the GET
  // ?sections= endpoint: on this store the GET returns an EMPTY cart-drawer
  // (the section is layout-injected, not in the page template), so ATC opened
  // the drawer empty. POST /cart/update.js with `sections` renders reliably —
  // the same mechanism qty/remove already use through /cart/change.js.
  function renderSection() {
    var attempt = function () {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ sections: 'cart-drawer', sections_url: window.location.pathname })
      }).then(function (r) {
        if (!r.ok) throw new Error('update.js HTTP ' + r.status);
        return r.json();
      });
    };
    return attempt()
      // Same transient-5xx retry as mutations — without it a failed render
      // leaves the drawer showing a stale (or page-load empty) state.
      .catch(function (err) {
        warn('render failed, retrying once:', err && err.message);
        return new Promise(function (res) { setTimeout(res, 650); }).then(attempt);
      })
      .then(function (data) {
        if (data && data.sections) applySectionHTML(data.sections['cart-drawer']);
        syncHeaderCount(data);
      })
      .catch(function (err) {
        warn('render failed after retry — drawer left as-is:', err && err.message);
      });
  }

  // Keep the Vue header's bag count in step with drawer mutations. The compiled
  // header only ever INCREMENTS (updateCartCount(delta), wired to adds by the
  // theme) — qty changes and removals here would leave the badge stale.
  // cartCount is reactive Vue data, so direct assignment repaints it.
  function syncHeaderCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    var vh = window.vueHeader;
    if (vh && typeof vh.cartCount === 'number') vh.cartCount = cart.item_count;
  }

  function showLineError(key, msg) {
    var row = lineRow(key);
    if (!row) return;
    var detail = row.querySelector('.vA-detail');
    if (!detail) return;
    var prev = detail.querySelector('.vA-lineError');
    if (prev) prev.remove();
    var el = document.createElement('div');
    el.className = 'vA-lineError';
    el.setAttribute('role', 'status');
    el.textContent = msg;
    detail.appendChild(el);
  }

  function restoreFocus(hint) {
    if (!isOpen() || !panel) return;
    var el = null;
    if (hint && hint.action && hint.key) {
      el = panel.querySelector('[' + hint.action + '][data-line-key="' + cssEsc(hint.key) + '"]');
    }
    // A control can re-render disabled (− at qty 1, + at max stock) — it can't
    // take focus, so land on the panel instead of silently dropping focus.
    if (el && el.disabled) el = null;
    if (el || panel) (el || panel).focus();
  }

  function warn() {
    try { console.warn.apply(console, ['[cart-drawer]'].concat([].slice.call(arguments))); } catch (_) {}
  }

  // The mutation core. Assumes `pending` is already set by the caller; clears it.
  function mutate(key, quantity, requestedQty, focusHint) {
    var row = lineRow(key);
    if (row) row.classList.add('is-busy');

    var body = JSON.stringify({
      id: key,
      quantity: quantity,
      sections: 'cart-drawer',
      sections_url: window.location.pathname
    });
    var attempt = function () {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: body
      }).then(function (r) {
        if (!r.ok) throw new Error('change.js HTTP ' + r.status);
        return r.json();
      });
    };

    attempt()
      // One silent retry after a beat: the cart endpoints throw transient 5xx
      // bursts (503s observed live) — a retry turns a scary error message into
      // a slightly slower update. `pending` stays set, so clicks queue safely.
      .catch(function (err) {
        warn('change failed, retrying once:', err && err.message);
        return new Promise(function (res) { setTimeout(res, 650); }).then(attempt);
      })
      .then(function (cart) {
        // Shopify clamps an over-stock increment to available qty and returns a
        // 200 with the adjusted cart (not an error) — detect + message.
        var stockMsg = null;
        if (requestedQty != null && cart.items) {
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].key === key) {
              if (cart.items[i].quantity < requestedQty) {
                stockMsg = 'Only ' + cart.items[i].quantity + ' left in stock.';
              }
              break;
            }
          }
        }
        applySectionHTML(cart.sections && cart.sections['cart-drawer']);
        syncHeaderCount(cart);
        var fresh = lineRow(key);                      // body swap normally drops
        if (fresh) fresh.classList.remove('is-busy');  // is-busy; belt-and-braces
        if (stockMsg) showLineError(key, stockMsg);
        restoreFocus(focusHint);
        pending = false;
      })
      .catch(function (err) {
        // Never leave the UI disagreeing with the server: re-render truth, then
        // surface a quiet, contained message under the line.
        warn('change failed after retry:', err && err.message);
        renderSection().then(function () {
          // If the reconcile render ALSO failed, the stale row keeps its
          // is-busy lock (dimmed, pointer-events none) forever — always free it.
          var stale = lineRow(key);
          if (stale) stale.classList.remove('is-busy');
          showLineError(key, 'Couldn’t update — please try again.');
          restoreFocus(focusHint);
          pending = false;
        });
      });
  }

  function onQtyClick(btn, dir) {
    if (pending) return;
    var key = btn.getAttribute('data-line-key');
    var row = lineRow(key);
    var span = row && row.querySelector('[data-line-qty]');
    var cur = span ? parseInt(span.textContent, 10) : NaN;
    if (!isFinite(cur)) return;
    var next = dir > 0 ? cur + 1 : Math.max(1, cur - 1);  // − clamps at 1 (REMOVE handles 0)
    if (next === cur) return;
    pending = true;
    if (span) span.textContent = next;                     // optimistic
    mutate(key, next, dir > 0 ? next : null, {
      action: dir > 0 ? 'data-cart-qty-increase' : 'data-cart-qty-decrease',
      key: key
    });
  }

  function onRemoveClick(btn) {
    if (pending) return;
    var key = btn.getAttribute('data-line-key');
    pending = true;
    var row = lineRow(key);
    if (!row || reducedMotion) { mutate(key, 0, null, null); return; }
    // Fade + collapse the row, then mutate — the re-render drops it for real.
    row.style.overflow = 'hidden';
    row.style.height = row.offsetHeight + 'px';
    void row.offsetWidth;
    row.style.transition = 'height .2s ease, opacity .2s ease, padding .2s ease';
    row.style.opacity = '0';
    row.style.height = '0px';
    row.style.paddingTop = '0px';
    row.style.paddingBottom = '0px';
    window.setTimeout(function () { mutate(key, 0, null, null); }, 200);
  }

  function open(opener) {
    if (!drawer || isOpen()) return;
    lastFocused = opener || document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll();
    playOpenMotion();
    // Focus the dialog PANEL (tabindex="-1", outline:none) — screen readers
    // announce the dialog and the first Tab lands on CLOSE, but no visible
    // focus ring appears on a mouse-driven open (focusing the CLOSE button
    // directly drew Safari's focus box around it — Flinn caught it).
    var delay = reducedMotion ? 0 : 40;
    window.setTimeout(function () { panel.focus(); }, delay);
  }

  function close() {
    if (!drawer || !isOpen()) return;
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus(); } catch (_) {}
    }
  }

  function isBagLinkTarget(target) {
    if (!target || target.nodeType !== 1) return false;
    var link = target.closest && target.closest(BAG_LINK_SELECTOR);
    if (!link) return false;
    // Only intercept links that resolve to /cart — leave /cart/add, /cart/change etc alone.
    var href = link.getAttribute('href') || '';
    if (href === '/cart' || /(^|\/)cart\/?($|\?|#)/.test(href)) return link;
    // Explicit .cart-icon a always counts as the bag icon.
    if (link.closest('.cart-icon')) return link;
    return false;
  }

  function onDocClickCapture(e) {
    // New tab / modifier keys → let the browser navigate to /cart normally.
    if (e.defaultPrevented) return;
    if (e.button && e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = isBagLinkTarget(e.target);
    if (!link) return;

    e.preventDefault();
    e.stopPropagation();
    open(link);
  }

  function onDocClick(e) {
    // Close button / backdrop / peek strip.
    var closer = e.target.closest && e.target.closest(CLOSE_SELECTOR);
    if (closer && drawer.contains(closer)) {
      e.preventDefault();
      close();
      return;
    }

    // Shipping & Returns accordion toggle.
    var accToggle = e.target.closest && e.target.closest('[data-cart-accordion-toggle]');
    if (accToggle && drawer.contains(accToggle)) {
      var expanded = accToggle.getAttribute('aria-expanded') === 'true';
      var panelId = accToggle.getAttribute('aria-controls');
      var accPanel = panelId ? document.getElementById(panelId) : null;
      accToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (accPanel) {
        if (expanded) accPanel.setAttribute('hidden', '');
        else accPanel.removeAttribute('hidden');
      }
      return;
    }

    // Qty steppers + REMOVE — delegated so they survive section re-renders.
    var dec = e.target.closest && e.target.closest('[data-cart-qty-decrease]');
    if (dec && drawer.contains(dec)) { onQtyClick(dec, -1); return; }
    var inc = e.target.closest && e.target.closest('[data-cart-qty-increase]');
    if (inc && drawer.contains(inc)) { onQtyClick(inc, 1); return; }
    var rm = e.target.closest && e.target.closest('[data-cart-remove]');
    if (rm && drawer.contains(rm)) { onRemoveClick(rm); return; }
  }

  function init() {
    drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    panel = drawer.querySelector('[data-cart-drawer-panel]');

    // Capture-phase click so we beat any Vue-header handler / analytics that
    // might try to navigate first. Non-modified plain left-clicks only.
    document.addEventListener('click', onDocClickCapture, true);
    document.addEventListener('click', onDocClick, false);
    document.addEventListener('keydown', onKeydown, false);

    // Auto-open on add-to-cart. Hook the network layer, not the buttons: every
    // add (PDP, grid, quick-view) funnels through jQuery $.ajax → /cart/add.js,
    // so ajaxSuccess catches them all regardless of when the button rendered
    // (survives infinite-scroll) and never touches #product-add-to-cart, so the
    // Klaviyo "Added to Cart" listener there keeps firing.
    if (window.jQuery) {
      var $j = window.jQuery;
      var isCartAdd = function (settings) {
        return /\/cart\/add(\.js)?(\?|$)/.test((settings && settings.url) || '');
      };

      // ATC feedback: the theme's "Processing…" box (message-modal snippet) is
      // shared with wishlist and sidebar filtering, so its default label stays
      // generic — relabel it for bag adds only and restore it when we let go.
      // A counter (not a flag) so rapid multi-size adds keep the box up until
      // the LAST pending add has rendered.
      var bagAddsInFlight = 0;
      var defaultLoadingLabel = null;
      var releaseLoadingBox = function () {
        try {
          var box = $j('.loading.cbox');
          // Drop the hold class BEFORE hiding — its display:block !important
          // would defeat the hide (and fadeOut's final display:none).
          box.removeClass('is-bag');
          if (reducedMotion) box.hide(); else box.stop(true, true).fadeOut(120);
          if (defaultLoadingLabel !== null) box.text(defaultLoadingLabel);
        } catch (err) {}
      };

      // One-round-trip ATC: fold the drawer's section render into the theme's
      // own /cart/add.js request. Measured (dev server, 5× medians): the render
      // adds ~50ms to the add itself vs ~500ms+ for a separate /cart/update.js
      // trip — halving click→open. Rewrites the request at the network layer;
      // no theme-file edits, and the theme's success handler ignores the extra
      // `sections` key in the response.
      $j.ajaxPrefilter(function (options) {
        if (!isCartAdd(options)) return;
        // Theme sends form-encoded strings ("quantity=1&id=123"); jQuery has
        // already stringified object data by prefilter time. Anything else
        // (FormData etc.) is left alone — the renderSection fallback covers it.
        if (options.data != null && typeof options.data !== 'string') return;
        options.data = (options.data ? options.data + '&' : '') +
          'sections=cart-drawer&sections_url=' + encodeURIComponent(window.location.pathname);
      });

      $j(document).ajaxSend(function (evt, xhr, settings) {
        if (!isCartAdd(settings)) return;
        bagAddsInFlight++;
        try {
          var box = $j('.loading.cbox');
          if (defaultLoadingLabel === null) defaultLoadingLabel = box.text();
          // is-bag holds the box visible (display:block !important) across the
          // theme's premature hide on /cart/add success — see cart-drawer.css.
          box.text('Adding to bag').addClass('is-bag');
        } catch (err) {}
      });

      $j(document).ajaxSuccess(function (evt, xhr, settings) {
        if (!isCartAdd(settings)) return;
        // Suppress the theme's legacy add-confirmation box for CART adds only —
        // the drawer is the confirmation now; wishlist keeps its own box.
        try { $j('.ajax-success-cbox').stop(true, true).hide(); } catch (err) {}
        // Prefer the section HTML folded into the add response (one round-trip,
        // see the prefilter above); fall back to a render trip for any add that
        // slipped past it. The is-bag class (set on ajaxSend) holds the feedback
        // box visible throughout, so feedback runs unbroken from click to open.
        var html = null;
        try {
          var data = xhr.responseJSON || JSON.parse(xhr.responseText);
          if (data && data.sections) html = data.sections['cart-drawer'];
        } catch (err) {}
        var ready;
        if (html) { applySectionHTML(html); ready = Promise.resolve(); }
        else ready = renderSection();
        ready.then(function () {
          open();
          if (--bagAddsInFlight <= 0) { bagAddsInFlight = 0; releaseLoadingBox(); }
        });
      });

      $j(document).ajaxError(function (evt, xhr, settings) {
        if (!isCartAdd(settings)) return;
        // The theme already hid the box and shows its own error box — just
        // restore the shared label for the other flows.
        if (--bagAddsInFlight <= 0) { bagAddsInFlight = 0; releaseLoadingBox(); }
      });
    }

    // Expose a small API for later phases (dev testing, programmatic open).
    window.SWCartDrawer = {
      open: function () { open(); },
      close: close,
      isOpen: isOpen,
      refresh: renderSection
    };
  }

  ready(init);
})();
